import type { Ref } from '@nuxtjs/composition-api';
import { computed, readonly, ref, useContext } from '@nuxtjs/composition-api';

import { ComposableFunctionArgs } from '~/composables/types';
import useApi from '~/composables/useApi';
import { useUiNotification } from '~/composables/useUiNotification';
import mask from '~/composables/utils/mask';
import { Logger } from '~/helpers/logger';
import { generateUserData } from '~/modules/account/helpers/generateUserData';
import { useCustomerStore } from '~/modules/account/stores/customer';
import { useCart } from '~/modules/checkout/composables/useCart';
import type { Customer } from '~/modules/GraphQL/types';

import { SigninWithPhoneNumber, UpdateCustomerPhoneNumber } from './UserCustomer.gql';
import {
  beginUserWrite,
  canApplyUserRead,
  getUserReadScope,
  hasCurrentUserSnapshot,
  invalidateUserReads,
  rememberUserReadSnapshot,
  sameUserReadIdentity,
  shareUserRead,
  userReadContractKey,
} from './userReadCoordinator';
import type {
  UseUserChangePasswordParams,
  UseUserErrors,
  UseUserInterface,
  UseUserLoadParams,
  UseUserLoginByPhoneNumberParams,
  UseUserLoginParams,
  UseUserLogoutParams,
  UseUserRegisterParams,
  UseUserUpdateUserParams,
} from './useUser';

/**
 * Allows loading and manipulating data of the current user.
 *
 * See the {@link UseUserInterface} for a list of methods and values available in this composable.
 */
export function useUser(): UseUserInterface {
  const customerStore = useCustomerStore();
  // @ts-ignore
  const { app, $recaptcha } = useContext();
  const { setCart } = useCart();
  const { send: sendNotification } = useUiNotification();
  const loading: Ref<boolean> = ref(false);
  const { query, mutate } = useApi();
  const userReadScope = getUserReadScope(customerStore, app);
  let loadSequence = 0;
  const getReadIdentity = () => {
    const { state } = app.context.$vsf.$magento.config;
    return {
      token: state.getCustomerToken?.() || '',
      store: state.getStore?.() || '',
      locale: state.getLocale?.() || '',
      currency: state.getCurrency?.() || '',
    };
  };

  const errorsFactory = (): UseUserErrors => ({
    updateUser: null,
    register: null,
    login: null,
    logout: null,
    changePassword: null,
    load: null,
  });
  const error: Ref = ref(errorsFactory());

  const setUser = (newUser: Customer) => {
    invalidateUserReads(userReadScope);
    customerStore.user = newUser;
    invalidateUserReads(userReadScope);
    Logger.debug('useUserFactory.setUser', newUser);
  };

  const resetErrorValue = () => {
    error.value = errorsFactory();
  };

  const updateCustomerEmail = async (credentials: { email: string; password: string }): Promise<void> => {
    const { errors } = await app.context.$vsf.$magento.api.updateCustomerEmail(credentials);

    if (errors) {
      throw errors.map((e) => e.message).join(',');
    }
  };

  // eslint-disable-next-line consistent-return
  const updateUser = async ({ user: providedUser, customQuery, customHeaders }: UseUserUpdateUserParams) => {
    Logger.debug('[Magento] Update user information', { providedUser, customQuery, customHeaders });
    resetErrorValue();
    const finishWrite = beginUserWrite(userReadScope);
    const writeOwner = getReadIdentity();

    try {
      loading.value = true;
      const { email: oldEmail } = customerStore.user;
      const { email, password, ...updateData } = providedUser;

      const userData = generateUserData(updateData);

      if (email && email !== oldEmail) {
        await updateCustomerEmail({
          email,
          password,
        });
      }

      if (!sameUserReadIdentity(writeOwner, getReadIdentity())) return;
      const { data, errors } = await app.context.$vsf.$magento.api.updateCustomer(userData, customQuery, customHeaders);
      if (!sameUserReadIdentity(writeOwner, getReadIdentity())) return;
      Logger.debug('[Result]:', { data });

      if (errors) {
        const allErrorMessages = errors.map((e) => e.message).join(',');
        Logger.error(allErrorMessages);
        error.value.updateUser = allErrorMessages;
      }

      customerStore.user = data?.updateCustomerV2?.customer || {};
      error.value.updateUser = null;
    } catch (err) {
      error.value.updateUser = err;
      Logger.error('useUser/updateUser', err);
    } finally {
      finishWrite();
      loading.value = false;
    }
  };
  const updateCustomerPhoneNumber = async (params: ComposableFunctionArgs<{ phone_number: string }>) => {
    Logger.debug('updateCustomerPhoneNumber', params);
    const finishWrite = beginUserWrite(userReadScope);
    const writeOwner = getReadIdentity();
    try {
      loading.value = true;

      const { data, errors } = await mutate(UpdateCustomerPhoneNumber, params);
      if (!sameUserReadIdentity(writeOwner, getReadIdentity())) return;
      Logger.debug('[updateCustomerPhoneNumber Result]:', data);
      Logger.debug('[updateCustomerPhoneNumber Result] errors:', errors);

      if (errors) {
        const allErrorMessages = errors.map((e) => e.message).join(',');
        Logger.error(allErrorMessages);
        error.value.updateUser = allErrorMessages;
      }

      customerStore.user.phone_number = params.phone_number || '';
      error.value.updateUser = null;
    } catch (err) {
      error.value.updateUser = err;
      Logger.error('useUser/updateUser', err);
    } finally {
      finishWrite();
      loading.value = false;
    }
  };

  const logout = async ({ customQuery = {}, customHeaders = {} }: UseUserLogoutParams = {}) => {
    Logger.debug('[Magento] useUserFactory.logout');
    resetErrorValue();
    invalidateUserReads(userReadScope);
    const logoutOwner = getReadIdentity();

    try {
      const apiState = app.context.$vsf.$magento.config.state;

      await app.context.$vsf.$magento.api.revokeCustomerToken(customQuery, customHeaders);
      if (logoutOwner.token !== getReadIdentity().token) return;

      apiState.removeCustomerToken();
      apiState.removeCartId();
      setCart(null);
      customerStore.setIsLoggedIn(false);
      customerStore.setUser(null);
      error.value.logout = null;
      customerStore.user = null;
    } catch (err) {
      error.value.logout = err;
      Logger.error('useUser/logout', err);
    } finally {
      invalidateUserReads(userReadScope);
    }
  };

  const load = async ({ customQuery = { customer: 'customer-query-user' }, customHeaders = {}, reuseExisting = false, force = false }: UseUserLoadParams = {}) => {
    Logger.debug('[Magento] useUser.load');
    resetErrorValue();
    const sequence = ++loadSequence;
    const owner = getReadIdentity();

    try {
      loading.value = true;
      if (!owner.token) {
        return null;
      }
      const contract = userReadContractKey(customQuery, customHeaders);
      if (reuseExisting && !force && hasCurrentUserSnapshot(userReadScope, owner, contract, customerStore.user)) {
        return customerStore.user;
      }
      const result = await shareUserRead(userReadScope, owner, contract, force, async (read) => {
        const userAtStart = customerStore.user;
        try {
          const { data, errors: responseErrors } = await app.context.$vsf.$magento.api.customer(customQuery, customHeaders);
          if (!canApplyUserRead(userReadScope, read, getReadIdentity()) || customerStore.user !== userAtStart) return null;
          customerStore.user = data?.customer ?? {};
          if (!canApplyUserRead(userReadScope, read, getReadIdentity())) return null;
          if (!responseErrors?.length) {
            rememberUserReadSnapshot(userReadScope, read, getReadIdentity(), contract, customerStore.user);
          }
          return customerStore.user;
        } catch (err) {
          // A late failure from an old owner/read must never revoke the new
          // login. Explicit custom-header reads do not own the base session.
          if (!canApplyUserRead(userReadScope, read, getReadIdentity()) || customerStore.user !== userAtStart) return null;
          if (Object.keys(customHeaders ?? {}).length === 0) await logout();
          throw err;
        }
      });
      if (sequence === loadSequence && sameUserReadIdentity(owner, getReadIdentity())) error.value.load = null;
      return sameUserReadIdentity(owner, getReadIdentity()) ? result : null;
    } catch (err) {
      if (sequence === loadSequence && sameUserReadIdentity(owner, getReadIdentity())) error.value.load = err;
      Logger.error('useUser/load failed');
    } finally {
      if (sequence === loadSequence) loading.value = false;
    }
    return sameUserReadIdentity(owner, getReadIdentity()) ? customerStore.user : null;
  };

  const loadUserStoreCredits = async (): Promise<string | null> => {
    Logger.debug('user.loadUserStoreCredits');
    resetErrorValue();
    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;
      const token = apiState.getCustomerToken();
      const currency = apiState.getCurrency();
      const currentStore = apiState.getStore();
      if (!token) {
        return null;
      }
      try {
        const customerHeader = { Authorization: token, 'Content-Currency': currency, Store: currentStore };
        const { data } = await query<any>(
          `query GetStoreCreditForCustomer {
              getStoreCreditForCurrentCustomer {
                  customer_id
                  storecredit_value
              }
          }
          `,
          null,
          null,
          customerHeader
        );
        Logger.debug('[GetStoreCreditForCustomer Result]:', { data });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          getStoreCreditForCurrentCustomer: { storecredit_value },
        } = data;
        customerStore.storeCredit = storecredit_value ?? '';
      } catch (errors) {
        // failed fetch data
        Logger.error('[GetStoreCreditForCustomer Failed]:', { errors });
      }
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('useUser/loadUserStoreCredits', err);
    } finally {
      loading.value = false;
    }
    return customerStore.storeCredit;
  };

  // eslint-disable-next-line @typescript-eslint/require-await,no-empty-pattern
  const login = async ({ user: providedUser, customQuery, customHeaders }: UseUserLoginParams): Promise<void> => {
    Logger.debug('[Magento] useUser.login');
    resetErrorValue();
    invalidateUserReads(userReadScope);

    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;

      const { data, errors } = await app.$vsf.$magento.api.generateCustomerToken(
        {
          email: providedUser.email.trim(),
          password: providedUser.password.trim(),
          recaptchaToken: providedUser.recaptchaToken,
        },
        customQuery || {},
        customHeaders || {}
      );
      Logger.debug('[Magento] login response received');

      if (errors && errors.length > 0) {
        const joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) =>
          sendNotification({
            icon: 'error',
            id: Symbol(`registration_error-${i}`),
            message: registerError.message,
            persist: false,
            title: 'Registration error',
            type: 'danger',
          })
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }

      customerStore.setIsLoggedIn(true);
      const maxAge = 3600 * 24 * 2; // 2 days expire
      apiState.setCustomerToken(data.generateCustomerToken.token, { maxAge });
      // 获取用户信息 -----
      load();

      // merge existing cart with customer cart
      // todo: move this logic to separate method
      const currentCartId = apiState.getCartId();
      const cart = await app.context.$vsf.$magento.api.customerCart();
      const newCartId = cart.data.customerCart.id;

      try {
        if (newCartId && currentCartId && currentCartId !== newCartId) {
          const { data: dataMergeCart } = await app.context.$vsf.$magento.api.mergeCarts({
            sourceCartId: currentCartId,
            destinationCartId: newCartId,
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setCart(dataMergeCart.mergeCarts);

          apiState.setCartId(dataMergeCart.mergeCarts.id);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setCart(cart.data.customerCart);
        }
      } catch {
        // Workaround for Magento 2.4.4 mergeCarts mutation error related with Bundle products
        // It can be removed when Magento 2.4.5 will be release
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setCart(cart.data.customerCart);
      }
      error.value.login = null;
    } catch (err) {
      error.value.login = err;
      Logger.error('useUser/login', err);
    } finally {
      invalidateUserReads(userReadScope);
      loading.value = false;
    }
  };
  const loginByPhoneNumber = async ({ user: providedUser }: UseUserLoginByPhoneNumberParams) => {
    Logger.debug('[Magento] useUser.login by phone number', providedUser);
    resetErrorValue();
    invalidateUserReads(userReadScope);

    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;

      const { data, errors } = await mutate<any>(SigninWithPhoneNumber, {
        phone_number: providedUser.phone_number,
        password: providedUser.password,
        recaptchaToken: providedUser.recaptchaToken,
      });
      Logger.debug('[Result]:', { data });

      if (errors && errors.length > 0) {
        const joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) =>
          sendNotification({
            icon: 'error',
            id: Symbol(`registration_error-${i}`),
            message: registerError.message,
            persist: false,
            title: 'Registration error',
            type: 'danger',
          })
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }

      customerStore.setIsLoggedIn(true);
      const maxAge = 3600 * 24 * 2; // 2 days expire
      apiState.setCustomerToken(data?.signinWithPhoneNumber.token, { maxAge });
      // 获取用户信息 -----
      load();

      // merge existing cart with customer cart
      // todo: move this logic to separate method
      const currentCartId = apiState.getCartId();
      const cart = await app.context.$vsf.$magento.api.customerCart();
      const newCartId = cart.data.customerCart.id;

      try {
        if (newCartId && currentCartId && currentCartId !== newCartId) {
          const { data: dataMergeCart } = await app.context.$vsf.$magento.api.mergeCarts({
            sourceCartId: currentCartId,
            destinationCartId: newCartId,
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setCart(dataMergeCart.mergeCarts);

          apiState.setCartId(dataMergeCart.mergeCarts.id);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setCart(cart.data.customerCart);
        }
      } catch {
        // Workaround for Magento 2.4.4 mergeCarts mutation error related with Bundle products
        // It can be removed when Magento 2.4.5 will be release
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setCart(cart.data.customerCart);
      }
      error.value.login = null;
    } catch (err) {
      error.value.login = err;
      Logger.error('useUser/login', err);
    } finally {
      invalidateUserReads(userReadScope);
      loading.value = false;
    }
  };

  // eslint-disable-next-line consistent-return
  const register = async ({ user: providedUser, customQuery, customHeaders }: UseUserRegisterParams): Promise<void> => {
    Logger.debug('[Magento] useUser.register', providedUser);
    resetErrorValue();
    invalidateUserReads(userReadScope);

    try {
      loading.value = true;

      const { email, password, recaptchaToken, ...baseData } = generateUserData(providedUser);

      const { data, errors } = await app.$vsf.$magento.api.createCustomer(
        {
          email,
          password,
          recaptchaToken,
          ...baseData,
        },
        customQuery || {},
        customHeaders || {}
      );

      Logger.debug('[Result]:', { data });

      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) =>
          sendNotification({
            icon: 'error',
            id: Symbol(`registration_error-${i}`),
            message: app.i18n.t(registerError.message) as string,
            persist: false,
            title: 'Registration error',
            type: 'danger',
          })
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }
      error.value.register = null;
      let loginRecaptchaToken = '';
      if ($recaptcha && recaptchaToken) {
        loginRecaptchaToken = await $recaptcha.getResponse();
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        customer: { customer_create_account_confirm },
      } = app.context.$vsf.$magento.config;

      if (customer_create_account_confirm) {
        return await new Promise((resolve) => {
          sendNotification({
            id: Symbol('registration_confirmation'),
            message: app.i18n.t('You must confirm your account. Please check your email for the confirmation link.') as string,
            persist: false,
            title: 'Registration confirmation',
            type: 'success',
            icon: 'check',
          });

          resolve();
        });
      }
      await login({ user: { email, password, recaptchaToken: loginRecaptchaToken }, customQuery: {} });
    } catch (err) {
      error.value.register = err;
      Logger.error('useUser/register', err);
    } finally {
      invalidateUserReads(userReadScope);
      loading.value = false;
    }
  };

  // eslint-disable-next-line consistent-return
  const changePassword = async (params: UseUserChangePasswordParams) => {
    Logger.debug('[Magento] useUser.changePassword', { currentPassword: mask(params.current), newPassword: mask(params.new) });
    resetErrorValue();
    const finishWrite = beginUserWrite(userReadScope);
    const writeOwner = getReadIdentity();

    try {
      loading.value = true;

      const { data, errors } = await app.context.$vsf.$magento.api.changeCustomerPassword(
        {
          currentUser: customerStore.user,
          currentPassword: params.current,
          newPassword: params.new,
        },
        params.customQuery,
        params?.customHeaders
      );
      if (!sameUserReadIdentity(writeOwner, getReadIdentity())) return;

      let joinedErrors = null;

      if (errors) {
        joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
      }

      Logger.debug('[Result] ', { data });

      customerStore.user = data?.changeCustomerPassword;
      error.value.changePassword = joinedErrors;
    } catch (err) {
      error.value.changePassword = err;
      Logger.error('useUser/changePassword', err);
    } finally {
      finishWrite();
      loading.value = false;
    }
  };

  return {
    setUser,
    updateUser,
    register,
    login,
    loginByPhoneNumber,
    logout,
    changePassword,
    load,
    loadUserStoreCredits,
    updateCustomerPhoneNumber,
    loading: readonly(loading),
    error: readonly(error),
    user: computed(() => customerStore.user),
    storeCredit: computed(() => customerStore.storeCredit),
    isAuthenticated: computed(() => customerStore.isLoggedIn),
  };
}

export default useUser;
export * from './useUser';
